import { useRef, useState } from 'react';
import {
  anexarArquivo,
  gravarDocumento,
  removerDocumentoLogicamente,
  urlDoArquivo,
  type Documento,
  type DonoDoDocumento,
  type TipoDeDocumento,
} from '@/lib/documentosRepo';

// Anexo de uma linha só.
//
// `DocumentosDoRegistro` continua sendo o bloco completo — nome, número,
// emissão, validade, arquivo —, e é o certo para o registro profissional, que
// tem todos esses campos. Aqui a pergunta é outra: o certificado da NR-35 e o
// RG que a profissão exige já têm nome e identidade vindos do catálogo; o que
// falta é o arquivo. Um formulário de cinco campos para anexar um PDF seria
// atrito onde a operação precisa de velocidade.
//
// A linha em `documentos` nasce no primeiro upload, e não antes: documento sem
// arquivo e sem data é uma linha que não responde nada, e encheria a aba de
// Documentos do condomínio com uma entrada por exigência de cada funcionário.

interface Props {
  /** Nulo enquanto o documento não existe — o primeiro upload o cria. */
  documento: Documento | null;
  dono: DonoDoDocumento;
  tipo: TipoDeDocumento;
  /** Nome do documento, vindo do catálogo. */
  nome: string;
  /** Vínculo com o catálogo: um ou outro, conforme o bloco (migration 0055). */
  treinamentoId?: string | null;
  profissaoDocumentoId?: string | null;
  somenteLeitura?: boolean;
  onMudou: () => void;
}

const TIPOS_ACEITOS = 'application/pdf,image/jpeg,image/png,image/webp';

export default function AnexoDoItem({
  documento,
  dono,
  tipo,
  nome,
  treinamentoId,
  profissaoDocumentoId,
  somenteLeitura,
  onMudou,
}: Props) {
  const entrada = useRef<HTMLInputElement | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const temArquivo = !!documento?.arquivoCaminho;

  const escolher = () => entrada.current?.click();

  const enviar = async (arquivo: File) => {
    setOcupado(true);
    setErro(null);
    try {
      // O documento precisa existir antes do upload: o caminho no bucket é
      // `<condominio>/<documento>/<arquivo>`, e sem id não há caminho.
      const alvo =
        documento ??
        (await gravarDocumento({
          dono,
          tipo,
          nome,
          treinamentoId: treinamentoId ?? null,
          profissaoDocumentoId: profissaoDocumentoId ?? null,
        }));

      await anexarArquivo(alvo, arquivo);
      onMudou();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Não foi possível anexar.');
    } finally {
      setOcupado(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  const baixar = async () => {
    if (!documento?.arquivoCaminho) return;
    const url = await urlDoArquivo(documento.arquivoCaminho);
    // URL assinada, válida por uma hora: o bucket é privado e não tem link fixo.
    if (url) window.open(url, '_blank', 'noopener');
    else setErro('Não foi possível abrir o arquivo.');
  };

  const remover = async () => {
    if (!documento) return;
    setOcupado(true);
    setErro(null);
    try {
      await removerDocumentoLogicamente(documento.id);
      onMudou();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Não foi possível remover.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex items-center gap-1">
        {temArquivo ? (
          <>
            <button
              type="button"
              onClick={() => void baixar()}
              title={documento?.arquivoNome ?? 'Baixar anexo'}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-green-200 bg-green-50 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors max-w-[10rem]"
            >
              <i className="ri-download-2-line text-[11px] shrink-0"></i>
              <span className="truncate">{documento?.arquivoNome ?? 'Baixar'}</span>
            </button>
            {!somenteLeitura && (
              <>
                <button
                  type="button"
                  onClick={escolher}
                  disabled={ocupado}
                  title="Substituir arquivo"
                  className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors disabled:opacity-50"
                >
                  <i className="ri-refresh-line text-xs"></i>
                  <span className="sr-only">Substituir o anexo de {nome}</span>
                </button>
                <button
                  type="button"
                  onClick={() => void remover()}
                  disabled={ocupado}
                  title="Remover anexo"
                  className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                  <span className="sr-only">Remover o anexo de {nome}</span>
                </button>
              </>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={escolher}
            disabled={ocupado || somenteLeitura}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-background-300/60 text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-attachment-2 text-[11px]"></i>
            {ocupado ? 'Enviando...' : 'Anexar'}
            <span className="sr-only"> arquivo de {nome}</span>
          </button>
        )}
      </div>

      <input
        ref={entrada}
        type="file"
        accept={TIPOS_ACEITOS}
        className="hidden"
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          if (arquivo) void enviar(arquivo);
        }}
      />

      {erro && (
        <p className="text-[11px] text-red-600 max-w-[12rem] text-right" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}
